import { useEffect, useState } from "react";
import { SelectFields } from "../../components/select/SelectFields";
import Button from "../../components/button/Button";
import { CheckBoxGroup, CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import { MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { GroupSelectModal } from "../userList/GroupSelectModal";
import { TextField } from "../../components/fields/Fields";
import { SearchCriteria } from "../../model/Users";
import companiesQuery from "../../firestore/Companies.query";
import Badge from "../../components/badges/Badges";
import { ReplyButton } from "../../model/ReplyButton";
import { Messages } from "../../model/Message";

type Props = {
  message: Messages;
	replyButton: ReplyButton | undefined;
  onSearch: (searchCondition: any) => void;
};

export const AttendanceSearch = ({
  message,
  replyButton,
  onSearch
}: Props) => {
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({});
  const [searchFlg, setSearchFlg] = useState(true);
  const [isOpenGroupSelect, setIsOpenGroupSelect] = useState(false);
  const [attendanceListValue, setAttendanceListValue] = useState<CheckBoxItem[]>([]);
	const [searchGroups, setSearchGroups] = useState<CheckBoxItem[]>([]);
	const [companyGroups, setCompanyGroups] = useState<CheckBoxItem[]>([]);
  const searchIcon = { icon: MagnifyingGlassIcon };
  const upIcon = { icon: ChevronUpIcon };
  const downIcon = { icon: ChevronDownIcon };

  // 既読状態フィルター用データ
  const readedList = [
    { id: "", name: "" },
    { id: "unread", name: "未読" },
    { id: "read", name: "既読" },
  ];

  // ステータスフィルター用データ
  const statusList = [
    { id: "", name: "" },
    { id: "undistributed", name: "配信外" },
  ];

  // アプリ利用フィルター用データ
  const availableList = [
    { id: "", name: "" },
    { id: "account", name: "利用可" },
    { id: "unaccount", name: "不可" },
  ];

  // 出欠フィルター用データ
  useEffect(() => {
		const f = async () => {
      if (message.tenantId !== '') {
        // 利用者が所属するグループを取得
        const groupList = (await companiesQuery.getGroups(message.tenantId)).filter((group) => group.deleteFlg === false);

        // 返信ボタンのリストを取得
        const updatedAttendanceList = [
          ...replyButton?.buttonName.map((name) => {
            // 「会場で参加」を「会場」に、「オンラインで参加」を「オンライン」に変更
            if (name === "会場で参加") {
              return { id: "会場で参加", title: "会場", value: false };
            }
            if (name === "オンラインで参加") {
              return { id: "オンラインで参加", title: "オンライン", value: false };
            }
            return { id: name, title: name, value: false};
          }) ?? [],
          { id: "未回答", title: "未回答", value: false },
        ];
        
        setAttendanceListValue(updatedAttendanceList);

        // 配信先グループ選択一覧
        const defaultGroupsValue = groupList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.name,
            value: false,
            disabled: item.deleteFlg,
          }
        })
        setCompanyGroups(defaultGroupsValue);
        setSearchGroups(defaultGroupsValue);
      }
		};
		f();
	}, [message]);

  const handleClick = (e: any) => {
    e.preventDefault();
    setSearchFlg(!searchFlg);
  };

  const onClickSearch = () => {
    const selectedAttendanceIds = attendanceListValue.filter(item => item.value).map(item => item.id);
    const selectedGroupIds = searchGroups.filter(group => group.value).map(group => group.id);

    const searchCondition = {
      // 名前
      lastName: searchCriteria.name === undefined || searchCriteria.name === ''
        ? null
        : searchCriteria.name,
      // 既読状態
      readed: searchCriteria.read === undefined || searchCriteria.read === ''
        ? null
        : searchCriteria.read === 'read'
          ? '既読'
          : '未読',
      // ステータス
      status: searchCriteria.status === undefined || searchCriteria.status === ''
        ? null
        : searchCriteria.status === 'undistributed'
          ? 'TRUE'
          : '',
      // アプリ利用
      account: searchCriteria.account === undefined || searchCriteria.account === ''
        ? null
        : searchCriteria.account === 'account'
          ? 'FALSE'
          : 'TRUE',
      // 出欠結果
      attendanceData: selectedAttendanceIds.length > 0 ? selectedAttendanceIds : null,
      // 所属グループ
      groupId: selectedGroupIds.length > 0 ? selectedGroupIds : null
    };

    onSearch(searchCondition);
  };

  const toggle = () => {
    setSearchGroups(companyGroups);
    setIsOpenGroupSelect(!isOpenGroupSelect);
  };

  const onSelectCancel = async () => {
    // 変更前のグループ選択状態をセット
    setCompanyGroups(searchGroups);
    setIsOpenGroupSelect(!isOpenGroupSelect);
  }

  return (
		<div className="flex flex-col">
			<div className="">
				<div className="inline-block min-w-full p-1 align-middle">
					<div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
						<table className="min-w-full table-fixed divide-y">
							<div className="sm:items-center p-2">
								<div className="flex justify-between cursor-pointer" onClick={handleClick}>
									<h2 className="flex font-semibold text-gray-900 items-center">
										<searchIcon.icon className={"flex-shrink-0 h-5 w-5"} aria-hidden="true" />
										検索
									</h2>
									{searchFlg ? (
										<upIcon.icon className={"flex-shrink-0 h-5 w-5"} aria-hidden="true" />
									) : (
										<downIcon.icon className={"flex-shrink-0 h-5 w-5"} aria-hidden="true" />
									)}
								</div>
								{searchFlg && (
									<>
										<h3 className="text-sm text-gray-500 mb-1">
											&nbsp;複数項目を入力した場合はAND検索となります
										</h3>
										<div className="flex flex-wrap gap-x-10">
											<div className="w-50 p-1">
												<div className="flex sm:items-center justify-start whitespace-nowrap">
													<h3 className="text-sm text-gray-900 mr-2">名前</h3>
													<div className="w-full">
														<TextField
															id="name"
															color="indigo"
															className={"h-10 w-full ml-11"}
															inputClassName={`h-8`}
															onChange={(e: any) =>
																setSearchCriteria({ ...searchCriteria, name: e.target.value })
															}
														/>
													</div>
												</div>
											</div>
											<div className="w-60 p-1 ml-5">
												<div className="flex sm:items-center justify-start whitespace-nowrap">
													<h3 className="text-sm text-gray-900 mr-2">既読状態</h3>
													<div className="w-full">
														<SelectFields
															id="read"
															color="indigo"
															className={"h-10 w-full"}
															selectItems={readedList}
															onChange={(e: any) =>
																setSearchCriteria({ ...searchCriteria, read: e.target.value })
															}
														/>
													</div>
												</div>
											</div>
											<div className="w-60 p-1 ml-3">
												<div className="flex sm:items-center justify-start whitespace-nowrap">
													<h3 className="text-sm text-gray-900 mr-2">ステータス</h3>
													<div className="w-full">
														<SelectFields
															id="status"
															color="indigo"
															className={"h-10 w-full"}
															selectItems={statusList}
															onChange={(e: any) =>
																setSearchCriteria({ ...searchCriteria, status: e.target.value })
															}
														/>
													</div>
												</div>
											</div>
											<div className="w-60 p-1">
												<div className="flex sm:items-center justify-start whitespace-nowrap">
													<h3 className="text-sm text-gray-900 mr-2">アプリ利用</h3>
													<div className="w-full">
														<SelectFields
															id="account"
															color="indigo"
															className={"h-10 w-full"}
															selectItems={availableList}
															onChange={(e: any) =>
																setSearchCriteria({ ...searchCriteria, account: e.target.value })
															}
														/>
													</div>
												</div>
											</div>
										</div>
										<div className="flex sm:items-center justify-start whitespace-nowrap mt-2">
											<h3 className="text-sm text-gray-900 mr-2">出欠結果</h3>
											<div className="w-3/4">
												<CheckBoxGroup
													children={undefined}
													setState={setAttendanceListValue}
													name="attendance"
													checkBoxItem={attendanceListValue}
													vertical={true}
													className={"flex-nowrap ml-4"}										
												/>
											</div>
										</div>
										<div className="w-full flex items-center justify-between mt-4">
											<div className="flex items-center flex-grow">
												<h3 className="text-sm text-gray-900">所属グループ</h3>
												&nbsp;
												<div className={`text-left items-start`}>
															<div className="h-12 w-[30rem] border-2 border-solid overflow-y-scroll">
														{searchGroups.filter((group) => group.value === true).map(
															(item: any) => {
																return (
																	<Badge color="blue">
																		{item.title}
																	</Badge>
																)
															}
														)}
													</div>
												</div>
												<Button
													className={"h-12 w-24 ml-4 pl-4 pr-4 text-sm sm:items-center rounded-full"}
													onClick={toggle}
													children={"グループ選択"}
												/>
											</div>
											<div className="flex sm:items-center justify-end mr-4">
												<Button
													className={"h-10 w-32 text-sm sm:items-center"}
													onClick={onClickSearch}
													children={"検索"}
												/>
											</div>
										</div>
										<h3 className="text-xs text-gray-500 mr-10">
											複数選択した場合いずれかに所属する利用者すべてが検索されます
										</h3>
										<GroupSelectModal
											open={isOpenGroupSelect}
											toggle={toggle}
											closeOnClickOutside
											companyGroupsValue={companyGroups}
											setState={setCompanyGroups}
											children={undefined}
											dispButtonFlg={true}
											onSelectCancel={onSelectCancel}
										/>
									</>
								)}
							</div>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};
